import { useContext, useEffect, useState } from 'react'

import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ExpandMore } from '@material-ui/icons'
import { MultiSectionDialog } from '@scoir/modals'

import ModalError from 'components/ModalError'
import { SortableList } from 'components/SortableList'

import OutreachMessageContext from '../OutreachMessageProvider/Context'
import { AutomatedMessagesByTrigger, Message } from '../types'
import { ReorderListItem } from './ReorderItem'

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow: 'none',
    padding: theme.spacing(1),
    // Override mui styling for disabled accordions
    backgroundColor: `${theme.palette.common.white} !important`,
  },
}))

const CONFIG = {
  'On-Follow': 'Triggered On Follow',
  'Marked-Applying': 'Triggered On Applying',
  'Marked-Applied': 'Triggered On Applied',
}

const PriorityReorderModal = () => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen(!open)
  const [accordionStates, setAccordionStates] = useState<{
    [key in keyof typeof CONFIG]: boolean
  }>({
    'On-Follow': false,
    'Marked-Applying': false,
    'Marked-Applied': false,
  })

  const { automatedMessagesByTrigger, updatePriorityOrder, state, dispatch } = useContext(OutreachMessageContext)
  // Save in state to avoid re-rendering while saving new order
  const [map, setMap] = useState<AutomatedMessagesByTrigger>()

  useEffect(() => {
    if (open) {
      setMap(automatedMessagesByTrigger)
    }
    if (!open) {
      dispatch({ type: 'set-reorder-error', hasReorderError: false })
    }
  }, [open, automatedMessagesByTrigger, dispatch])

  const toggleAccordion = (trigger: string) => {
    setAccordionStates((prev) => ({
      ...prev,
      [trigger]: !prev[trigger as keyof typeof CONFIG],
    }))
  }
  const toggleAllAccordions = (close: boolean) => {
    setAccordionStates({
      'On-Follow': close,
      'Marked-Applying': close,
      'Marked-Applied': close,
    })
  }

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleOpen}
      >
        Manage Priority
      </Button>
      {open && (
        <MultiSectionDialog
          open={open}
          onClose={handleOpen}
          scroll="paper"
          headerText="Manage Message Priority"
        >
          <MultiSectionDialog.Body>
            <Box
              display="flex"
              flexDirection="column"
              gridGap={16}
            >
              <Typography>
                Drag and drop to control which automated message Students and Parents receive. Each audience gets only
                one message per trigger type. The system sends the highest-priority (highest-ranked) message that
                matches their segmentation, moving down the list if no match is found. Be sure to include at least one
                broadly targeted message at the lowest priority so no students are missed.
              </Typography>
              <Box
                display="flex"
                flexDirection="column"
                gridGap={16}
              >
                {map &&
                  Object.keys(map).map((trigger, idx) => {
                    const messages = (map || [])[trigger as keyof typeof map] as Message[]

                    return (
                      <Paper
                        elevation={0}
                        key={`${trigger}-${idx}`}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          width="100%"
                          gridGap={8}
                        >
                          <Accordion
                            disabled={!(messages.length > 0)}
                            expanded={(accordionStates[trigger as keyof typeof CONFIG] && messages.length > 0) ?? false}
                            onChange={() => toggleAccordion(trigger)}
                            classes={{
                              root: classes.root,
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMore color={messages.length === 0 ? 'disabled' : 'inherit'} />}
                              aria-label={`Expand ${trigger}`}
                              aria-controls={`${trigger + idx}-accordion-content`}
                              id={`${trigger + idx}-accordion-header`}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                width="100%"
                              >
                                <Typography
                                  variant="h6"
                                  color="textPrimary"
                                >
                                  {CONFIG[trigger as keyof typeof CONFIG]}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <SortableList
                                items={normalizeItemsForDnd(messages)}
                                onChange={(items, activeIndex, overIndex) => {
                                  setMap({
                                    ...map,
                                    [trigger]: items,
                                  })
                                  updatePriorityOrder({ message: messages[activeIndex], priority: overIndex + 1 })
                                }}
                                renderItem={(item) => (
                                  <SortableList.Item id={item.id}>
                                    <SortableList.DragHandle>
                                      <ReorderListItem message={item} />
                                    </SortableList.DragHandle>
                                  </SortableList.Item>
                                )}
                              />
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      </Paper>
                    )
                  })}
              </Box>
            </Box>
          </MultiSectionDialog.Body>
          <MultiSectionDialog.Footer>
            <Box
              width="100%"
              display="flex"
              flexDirection="column"
            >
              {state.hasReorderError && <ModalError />}
              <Box
                display="flex"
                gridGap={8}
                alignItems="center"
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    toggleAllAccordions(Object.values(accordionStates).every((state) => state === false))
                  }}
                >
                  {Object.values(accordionStates).every((state) => state === false) ? 'Expand All' : 'Close All'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpen}
                >
                  Done
                </Button>
              </Box>
            </Box>
          </MultiSectionDialog.Footer>
        </MultiSectionDialog>
      )}
    </>
  )
}

const normalizeItemsForDnd = (items: Message[]) => {
  return items.map((item) => ({
    ...item,
    id: item.Id,
  }))
}
export default PriorityReorderModal
